import {
  type FormValidationResult as VeeValidateFormValidationResult,
  type Path,
  type PathValue,
  useField as internalUseField,
  useForm as internalUseForm
} from 'vee-validate'
import type { RecursiveUseFieldValue, UseFieldPath } from '~/types/utils'
import { omit } from 'es-toolkit'
import { fromPairs } from 'es-toolkit/compat'
import { generateRandomString } from '~/utils/helpers'
import { z, type ZodObject } from 'zod'
import type { PartialDeep } from 'type-fest'
import { useWebHaptics } from 'web-haptics/vue'

interface UseFormParams<T extends object> {
  readonly id: string
  readonly schema: ZodSchemaType<T>
  readonly refine?: (schema: ZodObject<ZodSchemaType<T>>) => ZodObject<ZodSchemaType<T>>
  initialValues?: PartialDeep<T>
}

interface FormValidationResult<
  T extends object
> extends VeeValidateFormValidationResult<T> {
  values: T
}

/**
 * Gets keys from zod schema recursively
 * @src https://github.com/colinhacks/zod/discussions/2134#discussioncomment-6581986
 * @param schema
 */
export interface FormWrapperProps<Model = never> {
  id: string
  schema: Record<UseFieldPath<Model>, string>
  errors: () => Partial<Record<Path<Model>, string | undefined>>
}

/**
 * Gets keys from zod schema recursively
 * @src https://github.com/colinhacks/zod/discussions/2134#discussioncomment-6581986
 * @param schema
 */
export function getZodSchemaKeys(schema: z.core.$ZodType): string[] {
  // check if `schema` is nullable or optional
  if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional) {
    return getZodSchemaKeys(schema.unwrap())
  }
  // check if `schema` is an array
  else if (schema instanceof z.ZodArray) {
    return getZodSchemaKeys(schema.element)
  }
  // check if schema is an object
  else if (schema instanceof z.ZodObject) {
    // get key/value pairs from schema
    const entries = Object.entries<z.ZodType>(schema.def.shape) // Adjusted: Uses Zod.ZodType as generic to remove instanceof check. Since .shape returns ZodRawShape which has Zod.ZodType as type for each key.
    // loop through key/value pairs
    return entries.flatMap(([key, value]) => {
      // get nested keys
      const nested = getZodSchemaKeys(value).map((subKey) => `${key}.${subKey}`)
      // return nested keys
      return nested.length ? nested : key
    })
  }
  // return empty array
  return []
}

export function useTypedForm<const T extends object>(
  formParams: NoInfer<UseFormParams<T>>
) {
  const id = useState(
    `form-id:${formParams.id}`,
    () => `${formParams.id}-${generateRandomString(5)}`
  )

  const { trigger } = useWebHaptics()

  const isLoading = ref(false)

  let implementedSchema = z.object(formParams.schema)
  if (formParams.refine) {
    implementedSchema = formParams.refine(implementedSchema)
  }

  const form = internalUseForm<T>({
    validationSchema: implementedSchema,
    initialValues: formParams.initialValues as any
  })

  const { errors, handleSubmit: innerHandleSubmit } = form

  function useField<K extends UseFieldPath<T>>(key: K) {
    return internalUseField<RecursiveUseFieldValue<T, K>>(key as string)
  }

  function setFieldValue<K extends UseFieldPath<T>>(
    key: K,
    value: RecursiveUseFieldValue<T, K> | undefined
  ) {
    form.setFieldValue(key as unknown as Path<T>, value as PathValue<T, Path<T>>)
  }

  const handleSubmit = ((cb, errorCb) => {
    const submitHandler = innerHandleSubmit(
      async (values, ctx) => {
        void trigger('success')
        await cb(values, ctx)
      },
      (ctx) => {
        void trigger('error')
        if (errorCb) {
          errorCb(ctx)
        }
      }
    )

    return async (...args) => {
      if (isLoading.value) {
        return
      }

      isLoading.value = true

      try {
        await submitHandler(...args)
      } finally {
        isLoading.value = false
      }
    }
  }) as typeof innerHandleSubmit

  const validate = async (): Promise<FormValidationResult<T>> => {
    return (await form.validate()) as FormValidationResult<T>
  }

  const omittedForm = omit(form, ['setFieldValue', 'validate', 'handleSubmit'])

  const keys = fromPairs(
    getZodSchemaKeys(implementedSchema).map((key) => [key, key])
  ) as Record<UseFieldPath<T>, string>

  const formProps: FormWrapperProps<T> = {
    id: id.value,
    schema: keys,
    errors: () => errors.value
  }

  return {
    useField,
    setFieldValue,
    validate,
    formProps,
    handleSubmit,
    isLoading,
    ...omittedForm
  }
}
